import React from "react";
import { Textarea } from "@headlessui/react";
import { useField } from "formik";
import RequiredMark from 'components/common/RequiredMark';

type ClassNameSlots = {
    wrapper?: string;
    label?: string;
    textarea?: string;
    footer?: string;
    error?: string;
    counter?: string;
};

/** A plain string is applied to the textarea; use the object form for other slots. */
type ClassNameProp = string | ClassNameSlots;

const normalizeClassName = (className?: ClassNameProp): ClassNameSlots =>
    typeof className === "string" ? { textarea: className } : (className ?? {});

export interface FormTextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "value"> {
    name: string;
    label?: string;
    /**
     * Omit inside a <Formik> tree — value / onChange / onBlur / error come from
     * context. Pass it (with onChange) to drive the field yourself.
     */
    value?: string;
    /** Supports Formik's `touched.x && errors.x` shape directly. */
    error?: string | false | null;
    helperText?: string;
    required?: boolean;
    /** Darker surface, matching FormInput's `darkBg`. */
    darkBg?: boolean;
    /** Shows a live `n/max` counter. Validation reports the overflow. */
    maxLength?: number;
    /**
     * True blocks typing past `maxLength` in the browser. Default false so the
     * yup `.max()` message surfaces instead of keystrokes silently vanishing.
     */
    hardLimit?: boolean;
    showCount?: boolean;
    className?: ClassNameProp;
}

/** The markup — fully controlled, no Formik knowledge. */
const TextareaField: React.FC<FormTextareaProps & { value: string }> = ({
    name,
    label,
    value,
    error,
    helperText,
    required = false,
    darkBg = false,
    maxLength,
    hardLimit = true,
    showCount,
    rows = 4,
    className,
    disabled,
    id,
    ...rest
}) => {
    const cls = normalizeClassName(className);
    const fieldId = id ?? name;
    const hasError = Boolean(error);
    const length = value?.length ?? 0;
    const withCount = showCount ?? typeof maxLength === "number";
    const footerVisible = hasError || Boolean(helperText) || withCount;

    return (
        <div className={`w-full ${cls.wrapper ?? ""}`}>
            {label && (
                <label
                    htmlFor={fieldId}
                    className={`text-sm text-neutral-medium tracking-wide font-medium block mb-2 ${cls.label ?? ""}`}
                >
                    {label}
                    {required && <RequiredMark />}
                </label>
            )}

            <Textarea
                id={fieldId}
                name={name}
                value={value ?? ""}
                rows={rows}
                disabled={disabled}
                maxLength={hardLimit ? maxLength : undefined}
                invalid={hasError}
                aria-describedby={
                    hasError ? `${fieldId}-error` : helperText ? `${fieldId}-helper` : undefined
                }
                className={[
                    "w-full rounded-lg p-3 text-sm resize-none transition-colors focus:outline-none",
                    "placeholder:font-normal placeholder:text-base placeholder:text-[#374151]",
                    darkBg ? "bg-primary-intemediate text-neutral" : "bg-primary-light text-neutral",
                    // One border colour in the base string; the variant overrides it.
                    // Two competing `border-*` classes would resolve by stylesheet
                    // order, not by position here.
                    "border border-primary-subtle data-[invalid]:border-red-500",
                    disabled ? "opacity-60 cursor-not-allowed" : "",
                    cls.textarea ?? "",
                ]
                    .filter(Boolean)
                    .join(" ")}
                {...rest}
            />

            {footerVisible && (
                <div className={`mt-1 flex items-start justify-between gap-3 ${cls.footer ?? ""}`}>
                    <span className="min-h-[16px]">
                        {hasError ? (
                            <span
                                id={`${fieldId}-error`}
                                className={`text-xs text-red-500 ${cls.error ?? ""}`}
                            >
                                {error}
                            </span>
                        ) : helperText ? (
                            <span id={`${fieldId}-helper`} className="text-xs text-neutral-medium">
                                {helperText}
                            </span>                          
                        ) : null}
                    </span>

                    {withCount && typeof maxLength === 'number' && (
                        <span
                            className={`shrink-0 text-xs tabular-nums ${length >= maxLength ? 'text-amber-500' : 'text-neutral-medium'
                                } ${cls.counter ?? ''}`}
                        >
                            {length}/{maxLength}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

/** Binds value / onChange / onBlur / error to Formik context. */
const FormikBoundTextarea: React.FC<FormTextareaProps> = ({ name, onChange, onBlur, ...rest }) => {
    const [field, meta] = useField<string>(name);

    return (
        <TextareaField
            {...rest}
            name={name}
            value={field.value ?? ""}
            onChange={(e) => {
                field.onChange(e);
                onChange?.(e); // optional side effect, after the Formik update
            }}
            onBlur={(e) => {
                field.onBlur(e);
                onBlur?.(e);
            }}
            error={meta.touched && meta.error ? meta.error : undefined}
        />
    );
};

const FormTextarea: React.FC<FormTextareaProps> = (props) =>
    // Explicit `value` means the caller owns the state. A call site never flips
    // between the two, so this picks one path for the field's lifetime.
    props.value !== undefined ? (
        <TextareaField {...props} value={props.value} />
    ) : (
        <FormikBoundTextarea {...props} />
    );

export default FormTextarea;